import { LotRecommendationType } from '@/utils/amplitude/analytics/item';
import { AmplitudePageName } from '@/utils/getPageNameFromPath';

export const TrackingEvents = {
    Account: {
        AccountCreated: 'Account Created',
        AddShippingAddressViewed: 'Add Shipping Address Viewed',
        CompleteAccountSetupBannerClicked: 'Complete Account Setup Banner Clicked',
        CompleteAccountSetupCTAClicked: 'Complete My Profile Clicked',
        EmailVerificationCodeSubmitted: 'Email Verification Code Submitted',
        MyAccountIconClicked: 'My Account Clicked',
        ResendEmailVerificationCodeClicked: 'Resend Email Verification Code Clicked',
        VerifyYourAddressViewed: 'Verify Your Address Viewed',
    },
    Auctioneer: {
        AskAQuestionPressed: 'Ask A Question Pressed',
        AuctioneerFollowed: 'Auctioneer Followed',
        AuctioneerUnfollowed: 'Auctioneer Unfollowed',
    },
    AuctionMarketingProgram: {
        AmpAvailable: 'AMP Available',
        AmpClicked: 'AMP Clicked',
        AmpSeen: 'AMP Seen',
    },
    AuthModal: {
        AddPaymentMethodViewed: 'Add Payment Method Viewed',
        LeadingBidderModalViewed: 'Leading Bidder Modal Viewed',
        OutbidViewed: 'Someone Has Outbid You Viewed',
        PlaceBidViewed: 'Place Bid Viewed',
        ReserveNotMetViewed: 'Reserve Not Met Viewed',
        ReviewAuctionRegistrationViewed: 'Review Your Auction Registration Viewed',
        ReviewBidViewed: 'Review Bid Viewed',
    },
    Bidding: {
        BackToLiveClicked: 'Back To Live Clicked',
        BidPlaced: 'Bid Placed',
        BidRetracted: 'Bid Retracted',
        BidSubmissionError: 'Bid Submission Error', // Capturing the error as shown to the user after an attempt to submit a bid (e.g., "User bid over allowed limit")
        BuyNowPressed: 'Buy Now Pressed',
        BuyNowRequestSent: 'Buy Now Request Sent',
        ListItemClicked: 'List Item Clicked',
        PlaceBidPressed: 'Place Bid Pressed',
        RequestHigherBidLimitClicked: 'Request Higher Bid Limit Clicked',
    },
    Browse: {
        CategoryViewed: 'Category Viewed',
    },
    Catalog: {
        AuctionCardClicked: 'Auction Card Clicked',
    },
    Checkout: {
        PayNowClicked: 'Pay Now Clicked',
    },
    CookieConsent: {
        CookieBannerViewed: 'Cookie Banner Viewed',
        CookiePreferencesDismissed: 'Cookie Preferences Dismissed',
        CookiePreferencesOpened: 'Cookie Preferences Opened',
        CookiePreferencesSaved: 'Cookie Preferences Saved',
    },
    CTA: {
        CTAClicked: 'CTA Clicked',
        LearnMorePurchaseProtection: 'Learn More: Purchase Protection',
    },
    GlobalNavigation: {
        HeaderAlertsClicked: 'Alerts Icon Clicked',
        HeaderCategoryClicked: 'Header Category Clicked',
        HeaderMyItemsClicked: 'My items Icon Clicked',
        HeaderWonItemsClicked: 'Won Items Icon Clicked',
    },
    HowToWin: {
        HowToWinClicked: 'How To Win Clicked',
    },
    Item: {
        ItemCardClicked: 'Item Card Clicked',
        ItemCardMenuClicked: 'Item Card Menu Clicked',
        ItemImageGalleryInteractionClicked: 'Image Gallery Interaction',
        ItemSaved: 'Lot Saved',
        ItemUnsaved: 'Saved Lot Removed',
        LotRecommendationClicked: 'Lot Recommendation Clicked',
        LotRecommendationSeen: 'Lot Recommendation Seen',
    },
    Notifications: {
        EnableSmsOnSavePrompt: 'Enable SMS On Save Prompt',
        SMSNotificationToggle: 'SMS Notification Toggle',
    },
    PageBottomReached: {
        PageBottomReached: 'Page Bottom Reached',
    },
    PurchaseProtection: {
        FileAClaim: 'File A Claim Clicked',
        LearnMoreDedicatedSupport: 'Learn More: Dedicated Support',
        LearnMoreItemPage: 'Learn More: Purchase Protection',
        LearnMoreNetwork: 'Learn More: Trust',
        LearnMorePurchaseProtection: 'Learn More: Purchase Protection',
        LearnMoreSecureBidding: 'Learn More: Secure Bidding',
    },
    Registration: {
        AddToCalendarPressed: 'Add To Calendar Clicked',
        AuctionRegistrationSubmitted: 'Auction Registration Submitted',
        RegisterForAuctionPressed: 'Register For Auction Pressed',
    },
    Search: {
        SearchCompleted: 'Search Completed',
        SearchFollowed: 'Search Followed',
        SearchRefined: 'Search Refined',
        SearchUnfollowed: 'Search Unfollowed',
    },
    SearchPage: {
        AllFiltersDrawerClosed: 'All Filters Drawer Closed',
        AllFiltersDrawerOpened: 'All Filters Drawer Opened',
        AuctionHouseSuggestionClicked: 'Auction House Suggestion Clicked',
        PreferencesFiltersDrawerClosed: 'Preferences Filters Drawer Closed',
        PreferencesFiltersDrawerOpened: 'Preferences Filters Drawer Opened',
        QuickFilterPillClicked: 'Quick Filter Pill Clicked',
        SearchSuggestionClicked: 'Search Suggestion Clicked',
    },
    Sharing: {
        ShareButtonClicked: 'Share Button Clicked',
    },
} as const;

export type BaseTrackingPayload = {
    pageName: AmplitudePageName;
    url: string;
};

export type TrackingPayload = BaseTrackingPayload & Record<string, any>;

export enum AnalyticsCatalogRegistrationStatus {
    Approved = 'Approved',
    ApprovedWithLimit = 'Approved With Limit',
    Declined = 'Declined',
    NotRegistered = 'Not Registered',
    Pending = 'Pending',
}

export enum AccountDetailsEventFlow {
    AccountCreation = 'Account Creation',
    AccountSettings = 'Account Settings',
    AuctionRegistration = 'Auction Registration',
    Bidding = 'Bidding',
    Checkout = 'Checkout',
}

export enum AuctionType {
    BuyNow = 'Buy Now',
    Live = 'Live',
    PreviewOnly = 'Preview Only',
    Timed = 'Timed',
    TimedPlus = 'Timed+',
}

export enum SearchCompletedTrigger {
    CatalogSearch = 'Catalog Search',
    Other = 'Other',
    SearchBar = 'Search Bar',
    UpcomingItems = 'Upcoming Items',
}

export type TrackLotRecommendationSeenPayload = {
    pageName: AmplitudePageName;
    recommendationIds: number[];
    recommendationName: LotRecommendationType;
};

export enum PriceResultsMatch {
    No = 'No',
    Yes = 'Yes',
}

export enum SearchContext {
    PriceResults = 'Price Results',
    UpcomingItems = 'Upcoming Items',
}

export enum QueryIntent {
    AuctionHouse = 'Auction House',
    None = 'None',
}

export const QueryIntentFromApi: Record<string, QueryIntent> = {
    auction_house_browse: QueryIntent.AuctionHouse,
} as const;
